import { Users, UserX, ShieldCheck, Pencil } from 'lucide-react'
import { DataTable, type Column } from '@/components/shared/DataTable'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { Button } from '@/components/ui/button'
import type { KsecUser } from './types'

interface UsuariosTableProps {
  data: KsecUser[]
  loading?: boolean
  error?: string | null
  total?: number
  canEdit?: boolean
  canDeactivate?: boolean
  canManagePermissions?: boolean
  onEdit?: (user: KsecUser) => void
  onDeactivate?: (user: KsecUser) => void
  onManagePermissions?: (user: KsecUser) => void
}

export function UsuariosTable({
  data,
  loading = false,
  error = null,
  total,
  canEdit = false,
  canDeactivate = false,
  canManagePermissions = false,
  onEdit,
  onDeactivate,
  onManagePermissions,
}: UsuariosTableProps) {
  const hasActions = canEdit || canDeactivate || canManagePermissions

  const columns: Column<KsecUser>[] = [
    {
      key: 'nickname',
      header: 'Usuario',
      cell: (row) => (
        <div>
          <p className="font-medium text-text-primary">{row.nickname}</p>
          <p className="text-xs text-text-tertiary">{row.email}</p>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (row) => <StatusBadge active={row.status === 'ACTIVE'} />,
    },
    {
      key: 'creationDate',
      header: 'Creado',
      cell: (row) =>
        new Date(row.creationDate).toLocaleDateString('es-PE', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }),
    },
    ...(hasActions
      ? [
          {
            key: 'actions' as keyof KsecUser,
            header: '',
            cell: (row: KsecUser) => (
              <div className="flex items-center justify-end gap-2">
                {canEdit && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onEdit?.(row)}
                    title="Editar usuario"
                  >
                    <Pencil size={14} />
                  </Button>
                )}
                {canManagePermissions && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onManagePermissions?.(row)}
                    title="Gestionar permisos"
                  >
                    <ShieldCheck size={14} />
                  </Button>
                )}
                {canDeactivate && row.status === 'ACTIVE' && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => onDeactivate?.(row)}
                    title="Desactivar usuario"
                  >
                    <UserX size={14} />
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ]

  return (
    <DataTable
      columns={columns}
      data={data}
      loading={loading}
      error={error}
      emptyIcon={<Users size={24} />}
      emptyTitle="Sin usuarios"
      emptyDescription="No se encontraron usuarios con los filtros actuales."
      footer={
        total !== undefined && total > 0 ? (
          <p className="text-sm text-text-tertiary">
            <span className="font-medium text-text-primary">{total}</span> usuarios en total
          </p>
        ) : undefined
      }
    />
  )
}
